import { pathOf, type PageModel } from '../PageModel';
import type { Rect } from './geometry';
import { drawPaper } from './templates';

/** Tile edge in CSS px. */
export const TILE_CSS = 256;
const MAX_TILE_BYTES = 160 * 1024 * 1024;
export const HIGHLIGHTER_ALPHA = 0.35;

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface Tile {
  key: string;
  pageId: string;
  tx: number;
  ty: number;
  canvas: OffscreenCanvas | HTMLCanvasElement;
  ctx: Ctx2D;
  valid: boolean;
  used: number;
}

function makeCanvas(size: number): { canvas: OffscreenCanvas | HTMLCanvasElement; ctx: Ctx2D } {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(size, size);
    return { canvas, ctx: canvas.getContext('2d', { alpha: true })! };
  }
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  return { canvas, ctx: canvas.getContext('2d')! };
}

/**
 * Cached rasters of committed ink (plus paper/template) per page, in square
 * tiles of `size` device pixels at one scale. Changing scale drops the cache.
 */
export class TileCache {
  size = TILE_CSS;
  scale = 1;
  private tiles = new Map<string, Tile>();
  /** Previous generation (before the last scale change), drawn scaled until replaced. */
  private stale: { scale: number; size: number; tiles: Map<string, Tile> } | null = null;
  private clock = 0;
  /** Milliseconds spent in the most recent raster() call. */
  lastRasterMs = 0;

  configure(dpr: number, scale: number): void {
    const size = Math.round(TILE_CSS * dpr);
    if (size === this.size && scale === this.scale) return;
    if (this.tiles.size > 0 && size === this.size) {
      this.stale = { scale: this.scale, size: this.size, tiles: this.tiles };
    } else {
      this.stale = null;
    }
    this.size = size;
    this.scale = scale;
    this.tiles = new Map();
  }

  /**
   * Old-generation tiles overlapping page-unit rect `r`, with the page-unit
   * rect each one covers. Used as a blurry placeholder after a zoom change.
   */
  staleTiles(pageId: string, r: Rect): { tile: Tile; x: number; y: number; unit: number }[] {
    const st = this.stale;
    if (!st) return [];
    const u = st.size / st.scale;
    const out: { tile: Tile; x: number; y: number; unit: number }[] = [];
    for (let tx = Math.floor(r.minX / u); tx <= Math.floor((r.maxX - 1e-6) / u); tx++) {
      for (let ty = Math.floor(r.minY / u); ty <= Math.floor((r.maxY - 1e-6) / u); ty++) {
        const t = st.tiles.get(this.key(pageId, tx, ty));
        if (t) out.push({ tile: t, x: tx * u, y: ty * u, unit: u });
      }
    }
    return out;
  }

  dropStale(): void {
    this.stale = null;
  }

  /** Tile edge in page units. */
  get unit(): number {
    return this.size / this.scale;
  }

  private key(pageId: string, tx: number, ty: number) {
    return `${pageId}|${tx}|${ty}`;
  }

  get(pageId: string, tx: number, ty: number): Tile | undefined {
    const t = this.tiles.get(this.key(pageId, tx, ty));
    if (t) t.used = ++this.clock;
    return t;
  }

  /** Marks tiles overlapping `r` (page units) stale. Their old pixels remain usable until re-rastered. */
  invalidate(pageId: string, r: Rect): void {
    const u = this.unit;
    for (let tx = Math.floor(r.minX / u); tx <= Math.floor(r.maxX / u); tx++) {
      for (let ty = Math.floor(r.minY / u); ty <= Math.floor(r.maxY / u); ty++) {
        const t = this.tiles.get(this.key(pageId, tx, ty));
        if (t) t.valid = false;
      }
    }
    this.stale = null;
  }

  invalidatePage(pageId: string): void {
    for (const t of this.tiles.values()) if (t.pageId === pageId) t.valid = false;
  }

  clear(): void {
    this.tiles.clear();
    this.stale = null;
  }

  raster(model: PageModel, tx: number, ty: number): Tile {
    const t0 = performance.now();
    const pageId = model.page.id;
    const key = this.key(pageId, tx, ty);
    let tile = this.tiles.get(key);
    if (!tile) {
      this.evict();
      const { canvas, ctx } = makeCanvas(this.size);
      tile = { key, pageId, tx, ty, canvas, ctx, valid: false, used: 0 };
      this.tiles.set(key, tile);
    }
    tile.used = ++this.clock;
    const { ctx } = tile;
    const u = this.unit;
    const s = this.scale;
    const { width, height } = model.page.size;
    const r: Rect = {
      minX: tx * u, minY: ty * u,
      maxX: Math.min((tx + 1) * u, width), maxY: Math.min((ty + 1) * u, height),
    };

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.size, this.size);
    ctx.setTransform(s, 0, 0, s, -tx * u * s, -ty * u * s);
    ctx.save();
    ctx.beginPath();
    ctx.rect(r.minX, r.minY, r.maxX - r.minX, r.maxY - r.minY);
    ctx.clip();
    drawPaper(ctx, model.page.template, model.page.size, r, s);
    for (const rs of model.query(r)) {
      const st = rs.stroke;
      ctx.globalAlpha = st.tool === 'highlighter' ? HIGHLIGHTER_ALPHA : 1;
      ctx.fillStyle = st.color;
      ctx.fill(pathOf(rs), 'nonzero');
    }
    ctx.globalAlpha = 1;
    ctx.restore();
    tile.valid = true;
    this.lastRasterMs = performance.now() - t0;
    return tile;
  }

  private evict(): void {
    const max = Math.max(24, Math.floor(MAX_TILE_BYTES / (this.size * this.size * 4)));
    if (this.tiles.size < max) return;
    const sorted = [...this.tiles.values()].sort((a, b) => a.used - b.used);
    for (const t of sorted.slice(0, this.tiles.size - max + 8)) this.tiles.delete(t.key);
  }

  get count(): number {
    return this.tiles.size;
  }
}
